import React, { useState } from 'react';
import {
  Spinner,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
} from '@fluentui/react-components';

import { useAppStore } from '@/stores/chatbotStore';
import { Document } from '@/types/api';

interface DocumentManagerProps {
  conversationId: string;
  compact?: boolean;
}

const DocumentManager: React.FC<DocumentManagerProps> = ({ conversationId, compact = false }) => {
  const {
    documents,
    isLoadingDocuments,
    isUploading,
    loadDocuments,
    uploadLocalFiles,
    deleteDocument,
  } = useAppStore();

  const [dragActive, setDragActive] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);

  const handleFiles = async (files: FileList) => {
    try {
      await uploadLocalFiles(conversationId, files);
      loadDocuments(conversationId);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) handleFiles(e.dataTransfer.files);
  };

  const handleDelete = (docId: string) => {
    setDeleteDocId(docId);
  };

  const confirmDelete = async () => {
    if (!deleteDocId) return;
    try {
      await deleteDocument(deleteDocId);
    } catch (error) {
      console.error('Delete failed:', error);
    }
    setDeleteDocId(null);
  };

  return (
    <div style={{ padding: '8px 0', color: '#1a1a1a' }}>
      {/* Upload area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        style={{
          margin: '0 12px', padding: '24px 16px',
          border: `2px dashed ${dragActive ? 'var(--color-primary)' : 'var(--color-border)'}`,
          borderRadius: 12, display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 8, textAlign: 'center',
          background: dragActive ? '#EEF0FA' : 'transparent',
        }}
      >
        {isUploading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280', fontSize: 13 }}>
            <Spinner size="small" />
            <span>Upload en cours...</span>
          </div>
        ) : (
          <>
            <input
              type="file"
              multiple
              accept=".pdf,.docx,.pptx,.xlsx,.txt"
              onChange={handleFileInput}
              style={{ display: 'none' }}
              id={`file-upload-${conversationId}`}
            />
            <button
              onClick={() => document.getElementById(`file-upload-${conversationId}`)?.click()}
              style={{
                padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8,
                background: '#ffffff', color: '#1a1a1a', fontSize: 13,
                fontFamily: 'inherit', cursor: 'pointer', fontWeight: 500,
              }}
            >
              Choisir des fichiers
            </button>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>ou glisser-deposer ici</span>
            <span style={{ fontSize: 11, color: '#d1d5db' }}>PDF, DOCX, PPTX, XLSX, TXT</span>
          </>
        )}
      </div>

      {/* Document list */}
      <div style={{ marginTop: 12, padding: '0 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 6, fontSize: 12, fontWeight: 500, color: '#6b7280' }}>
          <span>Documents ({documents.length})</span>
        </div>
        {isLoadingDocuments ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
            <Spinner size="tiny" />
          </div>
        ) : documents.length === 0 ? (
          <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>
            Aucun document uploade
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 8px', borderRadius: 6,
                }}
              >
                <span style={{ fontSize: 14, flexShrink: 0 }}>📄</span>
                <span
                  title={doc.name}
                  style={{
                    flex: 1, fontSize: 13, color: '#1a1a1a',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', minWidth: 0,
                  }}
                >
                  {doc.name}
                </span>
                <button
                  onClick={() => handleDelete(doc.id)}
                  title="Supprimer"
                  style={{
                    background: 'transparent', border: 'none',
                    color: '#9ca3af', cursor: 'pointer', padding: '2px 6px',
                    borderRadius: 4, fontSize: 16, lineHeight: 1, flexShrink: 0,
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete document confirmation dialog */}
      <Dialog open={!!deleteDocId} onOpenChange={(_, data) => { if (!data.open) setDeleteDocId(null); }}>
        <DialogSurface>
          <DialogTitle>Supprimer le document</DialogTitle>
          <DialogContent>
            <DialogBody>
              Supprimer ce document ? Cette action est irréversible.
            </DialogBody>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => setDeleteDocId(null)}>Annuler</Button>
            <Button appearance="primary" style={{ background: '#d13438' }} onClick={confirmDelete}>Supprimer</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default DocumentManager;
